import { createSearchParams } from 'react-router-dom';
import * as http from '../../utils/http';
import * as translator from '../../utils/translator';
import { TOOLTIP_MEDICATION, TOOLTIP_SERVICE_FEE, TOOLTIP_TREATMENT_FEE, TOOLTIP_DOCTOR, TOOLTIP_PHARMACY, CLICK_EXPAND_PRODUCTS } from '../../utils/consts';
import { loadCustomerFromSession } from '../../customHooks/useCustomer';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { getReorderPayloadExtras, trackReorderPaid } from '../Reorder/ReorderTrackingManager';
import type { PaymentContext, PaymentCallbacks, PaymentResult, PaymentErrorResponse, UserClickTracking, PaymentProvider } from './paymentTypes';
import { isStripeProvider } from './paymentTypes';
import { ADYEN_ERROR_STATUS_CODES, PENDING_RESULT_CODES, SUCCESS_RESULT_CODES, VOUCHER_ERROR_HANDLERS } from './paymentConstants';


// ============================================================================
// Browser Info Helper
// ============================================================================

export function getBrowserInfo() {
  return {
    colorDepth: window.screen.colorDepth,
    screenHeight: window.screen.height,
    screenWidth: window.screen.width,
    timeZoneOffset: new Date().getTimezoneOffset(),
  };
}

export function getFbpCookie(): string {
  const match = document.cookie.match(/_fbp=([^;]+)/);
  return match ? match[1] : '';
}


// ============================================================================
// Tracking Helper
// ============================================================================

// if(isCannabisCatalogRef.current){
export function trackCannabisBreakdown(tracking: UserClickTracking) {
  MainTracker.track('custom', 'user click breakdown', {
    page: 'Payment',
    'Click on delivery options': false,
    'Click on Medication tooltip': tracking.openedTooltips.has(TOOLTIP_MEDICATION),
    'Click on Service Fee tooltip': tracking.openedTooltips.has(TOOLTIP_SERVICE_FEE),
    'Click on Treatment Fee tooltip': tracking.openedTooltips.has(TOOLTIP_TREATMENT_FEE),
    'Click expand product (show/hide)': tracking.userClicks.has(CLICK_EXPAND_PRODUCTS),
    'Click on Partner Pharmacies pop-up': false,
    'Click on pharmacy details (expand)': false,
    'Click on terms': false,
    'Click on doctor details tooltip': tracking.openedTooltips.has(TOOLTIP_DOCTOR),
    'Click on product pharmacy tooltip': tracking.openedTooltips.has(TOOLTIP_PHARMACY),
  });
}


// ============================================================================
// Make Payment
// ============================================================================

export interface MakePaymentParams {
  provider?: PaymentProvider;
  gatewayState: any;
  context: PaymentContext;
  tracking?: UserClickTracking;  // maybe we can combine this with the context

  stripeParams?: StripeParams;
}

// Stripe-specific parameters
export interface StripeParams {
  confirmationToken: string;
  paymentMethodTypes: string[];
}

export async function makePayment({gatewayState, context, provider, tracking, stripeParams }: MakePaymentParams): Promise<PaymentResult> {
  // Track cannabis breakdown if applicable
  if (context.isCannabisCatalog && tracking) {
    trackCannabisBreakdown(tracking);
  }

  const clientHost = window.location.protocol + '//' + window.location.host;

  const payload: any = {
    gatewayState,
    clientHost,
    hashID: context.hashID,
    orderIds: context.orderIds,
    orderHashIDs: context.orderHashIDs,
    country: context.country,
    language: context.language,
    currencyCode: context.currencyCode,
    itemTitle: context.itemTitle,
    browserInfo: getBrowserInfo(),
    force3DS: context.force3DS,
    isRepay: context.isRepay,
    mailCode: context.mailCode,
    fbpCookie: getFbpCookie(),
    reorderSource: getReorderPayloadExtras(),
    sepaPositionVariant: context.sepaPositionVariant || '',
    ...(context.stripeLinkVariant && { stripeLinkVariant: context.stripeLinkVariant }),
    ...(context.cartExperiments && { cartExperiments: context.cartExperiments }),
  };

  // Add psp-specific data
  if (provider && isStripeProvider(provider)) {
    payload.provider = provider;
    payload.confirmationToken = stripeParams!.confirmationToken;
    payload.paymentMethodTypes = stripeParams!.paymentMethodTypes;
  }

  const response = await http.postToServer('payment/make_payment', payload, [http.MIDDLEWARES.CUSTOMER_ID]);

  // Set last paid category in events tracking for homepage drilldown pesonalization
  if (context?.catalog?.mention) {
    window.eventsTracking.setData("last_paid_category", context.catalog.mention);
  }

  return response.data;
}


// ============================================================================
// Handle Payment Result
// ============================================================================

export interface HandleResultParams {
  result: PaymentResult;
  context: PaymentContext;
  callbacks: PaymentCallbacks;
  navigate: (path: string) => void;

  // adyen drop_in status
  setDropinStatus?: (status: string) => void;

  // Overrides the thank-you-page redirect/reorder-tracking success path (e.g. for the repay flow)
  onSuccessOverride?: (result: PaymentResult) => void;
}

export async function handlePaymentResult({result, context, callbacks, navigate, setDropinStatus, onSuccessOverride}: HandleResultParams) {
  let dropInStatus: 'ready' | 'success' | null = 'ready';

  // Handle express unavailable
  if (result.isExpressUnavailable) {
    callbacks.onExpressUnavailable(result.latePaymentReason || '', result.isNextDayAvailable ?? false);
  }

  // Handle express prescription review unavailable
  else if (result.isExpressPrescriptionUnavailable) {
    callbacks.onExpressPrescriptionReviewUnavailable();
  }

  // Handle action required (3DS, redirect, etc.)
  else if (result.providerResponse?.action) {
    await callbacks.onPaymentAction(result.providerResponse.action);
  }

  // Handle success
  else if (SUCCESS_RESULT_CODES.includes(result.resultCode)) {
    if (onSuccessOverride) {
      onSuccessOverride(result);
    } else {
      handlePaymentSuccess(result, context, callbacks, navigate);
    }
    dropInStatus = 'success';
  }

  else if (PENDING_RESULT_CODES.includes(result.resultCode)) {
    handlePaymentPending(result, context, navigate);
    dropInStatus = 'success';
  }

  // Handle refused
  else if (['Refused', 'Error'].includes(result.resultCode)) {
    handlePaymentRefused(result, context, callbacks);
  }

  // Handle other failures
  else {
    callbacks.showErrorNotification(
      translator.translate(context.language, 'default', 'StickyNotification/danger_title'),
      result.refusalReason || result.message || 'Payment failed'
    );
  }

  setDropinStatus?.(dropInStatus);
}


// ============================================================================
// Handle Payment Success
// ============================================================================

function handlePaymentSuccess(result: PaymentResult, context: PaymentContext, callbacks: PaymentCallbacks, navigate: (path: string) => void) {
  if (result.isUpgradedToPremium) {
    const upsellVariant = localStorage.getItem('abtest_mobile_upsell_modal_variant') || null;
    MainTracker.track('custom', 'Upsell_Popup_Converted', { variant: upsellVariant, price_scenario: null, "Payment Service Provider": context.paymentServiceProvider });
  }
  // Track reorder paid (reads sp/reorder/saved from URL)
  trackReorderPaid(context.orderPrice * context.currencyRate, context.catalog.mention);

  const urlParams = new URLSearchParams(window.location.search);
  const tpp = localStorage.getItem('dhl_next_day_displayed');
  localStorage.removeItem('dhl_next_day_displayed');
  const params = {
    ...Object.fromEntries(urlParams.entries()), // Current query parameters
    result: result.resultCode,
    thankyouHash: context.thankyouHash,
    a: String(context.customerData.age),
    g: context.customerData.gender ? 'm' : 'f',
    ord: context.customerData.paidOrdersCount ? '0' : '1', // data is queried before current order is paid, ord determines whether this is first paid order
    cat: context.catalog.mention,
    pm: result.paymentMethod?.type || '',
    psp: context.paymentServiceProvider,
    onlyPrescription: String(context.isOrderOnlyPrescription),
    ...(tpp ? { tpp } : {}),
  };

  setTimeout(() => {
    // save paid in client - in order to not load payment page
    window.eventsTracking.setData('last_paid_order', context.hashID);
    window.eventsTracking.setData('last_order_action', Date.now());

    navigate(`/thank-you/${context.country}/${context.language}/${context.hashID}?${createSearchParams(params).toString()}`);
  }, 3500);

  callbacks.onPaymentSuccess(result);
}


// ============================================================================
// Handle Payment Pending
// ============================================================================

function handlePaymentPending(result: PaymentResult, context: PaymentContext, navigate: (path: string) => void) {
  const urlParams = new URLSearchParams(window.location.search);
  const params = {
    ...Object.fromEntries(urlParams.entries()), // Current query parameters
    result: result.resultCode,
    thankyouHash: context.thankyouHash,
    a: String(context.customerData.age),
    g: context.customerData.gender ? 'm' : 'f',
    ord: context.customerData.paidOrdersCount ? '0' : '1',
    cat: context.catalog.mention,
    pm: result.paymentMethod?.type || '',
    psp: context.paymentServiceProvider,
    onlyPrescription: String(context.isOrderOnlyPrescription),
  };

  navigate(`/thank-you/${context.country}/${context.language}/${context.hashID}?${createSearchParams(params).toString()}`);
}


// ============================================================================
// Handle Payment Refused
// ============================================================================

function handlePaymentRefused(result: PaymentResult, context: PaymentContext, callbacks: PaymentCallbacks) {
  const provider = context.paymentServiceProvider;

  let errorMsg = translator.translate(context.language, 'payment', `${provider}/refusalCode/DEFAULT`);
  
  // Handle Adyen errors - use refusalReasonCode for translation lookup
  if (provider === 'adyen' && result.refusalReasonCode) {
    const refusalCode = Number(result.refusalReasonCode);
    const translatedMsg = translator.translate(context.language, 'payment', `${provider}/refusalCode/${refusalCode}`);
    errorMsg = translatedMsg || errorMsg;
  }

  // Handle Stripe errors - use the message directly if available
  else if (isStripeProvider(provider) && result.stripeError) {
    const { code, decline_code, message } = result.stripeError;
    
    // Try to get a translated message for the specific error code/decline_code
    if (code) {
      const translatedByCode = translator.translate(context.language, 'payment', `stripe/errorCode/${code}`);
      if (translatedByCode) errorMsg = translatedByCode;
    }
    if (decline_code) {
      const translatedByDeclineCode = translator.translate(context.language, 'payment', `stripe/declineCode/${decline_code}`);
      if (translatedByDeclineCode) errorMsg = translatedByDeclineCode;
    }

    // If no translation found, use the Stripe message directly
    if (errorMsg === translator.translate(context.language, 'payment', `${provider}/refusalCode/DEFAULT`) && message) {
      errorMsg = message;
    }
  } 

  callbacks.showErrorNotification(translator.translate(context.language, 'default', 'StickyNotification/danger_title'), errorMsg);
  callbacks.setPaymentRefused(true);
  callbacks.onPaymentRefused(result);
}


// ============================================================================
// Handle Payment Error
// ============================================================================

export interface HandleErrorParams {
  error: any;
  context: PaymentContext;
  callbacks: PaymentCallbacks;

  // adyen drop_in status
  setDropinStatus?: (status: string) => void;
}

export function handlePaymentError({error, context, callbacks, setDropinStatus}: HandleErrorParams) {
  const provider = context.paymentServiceProvider || 'adyen';
  const errorResponse: PaymentErrorResponse | undefined = error?.response?.data;
  const status = errorResponse?.status;

  // Handle voucher errors
  if (status && VOUCHER_ERROR_HANDLERS[status]) {
    const handler = VOUCHER_ERROR_HANDLERS[status];

    callbacks.showErrorNotification(
      translator.translate(context.language, 'payment', handler.titleKey),
      translator.translate(context.language, 'payment', handler.contentKey)
    );
    callbacks.setPaymentRefused(true);
    
    setDropinStatus?.('ready');
    MainTracker.track('custom', 'Payment Post Failure', { Reason: handler.reason, "Payment Service Provider": context.paymentServiceProvider });
    return;
  }

  // Handle Stripe errors from response
  const stripeError = errorResponse?.stripeError;
  if (isStripeProvider(provider) && stripeError) {
    let errorMsg = translator.translate(context.language, 'payment', 'stripe/refusalCode/DEFAULT');
    
    // Try to get a translated message for the specific error code/decline_code
    if (stripeError.code) {
      const translatedByCode = translator.translate(context.language, 'payment', `stripe/errorCode/${stripeError.code}`);
      if (translatedByCode) errorMsg = translatedByCode;
    }
    if (stripeError.decline_code) {
      const translatedByDeclineCode = translator.translate(context.language, 'payment', `stripe/declineCode/${stripeError.decline_code}`);
      if (translatedByDeclineCode) errorMsg = translatedByDeclineCode;
    }
    
    // If no translation found, use the Stripe message directly
    if (stripeError.message && errorMsg === translator.translate(context.language, 'payment', 'stripe/refusalCode/DEFAULT')) {
      errorMsg = stripeError.message;
    }

    callbacks.showErrorNotification(translator.translate(context.language, 'default', 'StickyNotification/danger_title'), errorMsg);
    callbacks.setPaymentRefused(true);

    MainTracker.track('custom', 'Payment Post Failure', { Reason: `${provider}: ${stripeError.code || stripeError.decline_code}`, "Payment Service Provider": context.paymentServiceProvider });
    return;
  }

  // Handle Adyen-specific errors (422)
  if (provider === 'adyen' && status && ADYEN_ERROR_STATUS_CODES.includes(Number(status))) {
    callbacks.showErrorNotification(
      translator.translate(context.language, 'default', 'StickyNotification/danger_title'),
      translator.translate(context.language, 'payment', `${provider}/refusalCode/DEFAULT`)
    );
    callbacks.setPaymentRefused(true);

    setDropinStatus?.('ready');
    MainTracker.track('custom', 'Payment Post Failure', { Reason: `${provider} refusal`, "Payment Service Provider": context.paymentServiceProvider });
    return;
  }

  // Handle a second payment attempt on the same order within a few seconds (double submit)
  if (status === 'duplicate_payment_attempt') {
    callbacks.showErrorNotification(
      translator.translate(context.language, 'default', 'StickyNotification/danger_title'),
      translator.translate(context.language, 'payment', 'duplicate_attempt/content'),
      { sticky: true }
    );

    setDropinStatus?.('ready');
    MainTracker.track('custom', 'Payment Post Failure', { Reason: 'duplicate payment attempt', "Payment Service Provider": context.paymentServiceProvider });
    return;
  }

  // Handle refresh page
  if (status === 'refresh_page') {
    callbacks.onRefreshPage();
    return;
  }

  // Handle out of stock
  if (errorResponse?.logLevel === 'warn' && status === 'out_of_stock' && errorResponse.cartData) {
    callbacks.onOutOfStock(errorResponse.cartData);
    return;
  }

  // Handle unknown errors
  MainTracker.track('custom', 'Payment Post Failure', { Reason: 'unknown error', "Payment Service Provider": context.paymentServiceProvider });
  window.clientLogger?.error?.('error in payment', error);
  setDropinStatus?.('ready');
  callbacks.onError(error);
}


// ============================================================================
// Validate Customer Session
// ============================================================================

export function validateCustomerSession(): boolean {
  const customerData = loadCustomerFromSession();
  const token = customerData?.token;
  return !!token;
}


// ============================================================================
// Adyen - Payment Details API Call (for 3DS)
// ============================================================================

export async function submitPaymentDetails(data: any): Promise<PaymentResult> {
  const response = await http.postToServer('payment/payment_details', data, [http.MIDDLEWARES.CUSTOMER_ID]);
  return response.data;
}


// ============================================================================
// ID Verification Workflow
// ============================================================================

export interface FetchIdVerificationWorkflowResponse {
  status: string;
  workflowHref?: string;
  alreadyVerified?: boolean;
}

export async function fetchIdVerificationWorkflow(hashID: string, language: string): Promise<FetchIdVerificationWorkflowResponse> {
  const response = await http.postToServer(
    'payment/id_verification_workflow',
    { hashID, language },
    [http.MIDDLEWARES.CUSTOMER_ID]
  );
  return response.data;
}